/**
 * CTRL_02 Plugin - Data Quality Monitoring
 * Custom UI with quality metrics dashboard and trend charts
 * Extends GenericConfigPanel with visual quality indicators
 */

import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Progress,
  Statistic,
  Alert,
  Table,
  Tag,
  Space,
  Tooltip,
} from 'antd';
import {
  CheckCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import GenericConfigPanel from '../GenericConfigPanel';
import { colors } from '../../../../theme/designTokens';

/**
 * Quality Metric Card
 */
function QualityMetricCard({ title, score, threshold, icon, description }) {
  const getStatus = () => {
    if (score >= threshold) return { color: colors.status.pass, status: 'success' };
    if (score >= threshold * 0.8) return { color: colors.status.warning, status: 'warning' };
    return { color: colors.status.fail, status: 'error' };
  };

  const { color, status } = getStatus();

  return (
    <Card size="small">
      <Statistic
        title={
          <Space>
            {icon}
            <Tooltip title={description}>{title}</Tooltip>
          </Space>
        }
        value={score}
        suffix="%"
        valueStyle={{ color }}
      />
      <Progress
        percent={score}
        strokeColor={color}
        status={status}
        size="small"
        style={{ marginTop: 8 }}
      />
      <div style={{ marginTop: 8, fontSize: 12, color: colors.text.secondary }}>
        Threshold: {threshold}%
      </div>
    </Card>
  );
}

/**
 * Data Quality Dashboard Component
 */
function DataQualityDashboard({ config, projectId }) {
  const [metrics, setMetrics] = useState({
    completeness: 0,
    validity: 0,
    consistency: 0,
    accuracy: 0,
    timeliness: 0,
  });
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (config?.enabled && projectId) {
      fetchQualityMetrics();
    }
  }, [config, projectId]);

  const fetchQualityMetrics = async () => {
    setLoading(true);
    try {
      // Simulate API call - replace with actual endpoint
      // const response = await fetch(`/api/trustgrid/projects/${projectId}/data-quality`);
      // const data = await response.json();

      // Mock data for now
      setTimeout(() => {
        setMetrics({
          completeness: 97.5,
          validity: 92.3,
          consistency: 95.8,
          accuracy: 89.2,
          timeliness: 94.1,
        });
        setIssues([
          {
            id: 1,
            field: 'user_email',
            issue: 'Invalid format',
            severity: 'high',
            count: 45,
          },
          {
            id: 2,
            field: 'created_at',
            issue: 'Missing values',
            severity: 'medium',
            count: 12,
          },
        ]);
        setLoading(false);
      }, 1000);
    } catch (error) {
      console.error('Failed to fetch quality metrics:', error);
      setLoading(false);
    }
  };

  const qualityMetrics = [
    {
      key: 'completeness',
      title: 'Completeness',
      icon: <CheckCircleOutlined />,
      description: 'Percentage of non-null values',
      threshold: config?.completeness_threshold || 95,
    },
    {
      key: 'validity',
      title: 'Validity',
      icon: <CheckCircleOutlined />,
      description: 'Percentage of valid format values',
      threshold: config?.validity_threshold || 90,
    },
    {
      key: 'consistency',
      title: 'Consistency',
      icon: <SyncOutlined />,
      description: 'Data consistency across sources',
      threshold: 90,
    },
    {
      key: 'accuracy',
      title: 'Accuracy',
      icon: <CheckCircleOutlined />,
      description: 'Data accuracy compared to source',
      threshold: 90,
    },
    {
      key: 'timeliness',
      title: 'Timeliness',
      icon: <SyncOutlined />,
      description: 'Data freshness and currency',
      threshold: 90,
    },
  ];

  const issueColumns = [
    {
      title: 'Field',
      dataIndex: 'field',
      key: 'field',
    },
    {
      title: 'Issue',
      dataIndex: 'issue',
      key: 'issue',
    },
    {
      title: 'Severity',
      dataIndex: 'severity',
      key: 'severity',
      render: (severity) => {
        const color = severity === 'critical' || severity === 'high' ? 'red' : 'orange';
        return <Tag color={color}>{severity.toUpperCase()}</Tag>;
      },
    },
    {
      title: 'Count',
      dataIndex: 'count',
      key: 'count',
      align: 'right',
    },
  ];

  if (!config?.enabled) {
    return (
      <Alert
        message="Data Quality Monitoring Disabled"
        description="Enable data quality monitoring in the configuration below to see metrics."
        type="info"
        showIcon
      />
    );
  }

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {/* Quality Metrics */}
      <div>
        <h3>Quality Metrics</h3>
        <Row gutter={[16, 16]}>
          {qualityMetrics
            .filter((metric) => config?.quality_checks?.includes(metric.key))
            .map((metric) => (
              <Col key={metric.key} xs={24} sm={12} lg={8}>
                <QualityMetricCard
                  title={metric.title}
                  score={metrics[metric.key]}
                  threshold={metric.threshold}
                  icon={metric.icon}
                  description={metric.description}
                />
              </Col>
            ))}
        </Row>
      </div>

      {/* Data Quality Issues */}
      {issues.length > 0 && (
        <div>
          <h3>Active Issues</h3>
          <Table
            columns={issueColumns}
            dataSource={issues}
            rowKey="id"
            size="small"
            pagination={false}
            loading={loading}
          />
        </div>
      )}
    </Space>
  );
}

/**
 * CTRL_02 Plugin Component
 */
function CTRL_02_Plugin({ projectId, controlCode }) {
  const [config, setConfig] = useState({
    enabled: false,
    quality_checks: ['completeness', 'validity', 'consistency'],
    completeness_threshold: 95,
    validity_threshold: 90,
  });

  return (
    <div>
      {/* Quality Dashboard */}
      <DataQualityDashboard config={config} projectId={projectId} />

      {/* Generic Config Panel - usePlugin={false} prevents infinite loop */}
      <GenericConfigPanel projectId={projectId} controlCode={controlCode} usePlugin={false} />
    </div>
  );
}

export default CTRL_02_Plugin;
